import {
  getGetDepartmentByIDQueryKey,
  getGetTravellerByIDQueryKey,
  TravellerWithCollectionMembershipsDTO,
  useDeleteCollectionMemberByID,
} from '@/api/trafikportalen';
import { useIntl } from 'react-intl';
import {
  isPartialDeleteTravellerProductsError,
  useDeleteTravellerAssociatedProducts,
} from '@/hooks/useDeleteTravellerAssociatedProducts';
import { useTenantID } from '@/hooks/useTenantID';
import { useCallback, useMemo } from 'react';
import { toast } from '@/hooks/useToast';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from '@tanstack/react-router';
import { DeleteConfirmationForm } from '../DeleteConfirmationForm';
import { useQueryClient } from '@tanstack/react-query';
import { TravellerInactivateForm } from './TravellerInactivateForm';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { FormSection } from '../FormSection';
import { getTravellerStatus } from '@/helpers/status';

interface Props {
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const TravellerAdvanced = ({ traveller }: Props) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const deleteCollectionMember = useDeleteCollectionMemberByID();
  const { deleteAssociatedProducts } = useDeleteTravellerAssociatedProducts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const hasNonSchoolMemberships = useMemo(() => {
    return traveller.memberships.some(
      ({ collection_type }) => collection_type === 'master',
    );
  }, [traveller.memberships]);

  const onDeleteTraveller = useCallback(async () => {
    if (!traveller) {
      return;
    }

    let associatedProductsDeleted = false;

    try {
      await deleteAssociatedProducts(traveller);
      associatedProductsDeleted = true;

      await Promise.all(
        traveller.memberships.map((membership) =>
          deleteCollectionMember.mutateAsync({
            collectionMemberID: membership.collection_member_id,
          }),
        ),
      );

      await Promise.all(
        traveller.memberships.map((membership) =>
          queryClient.invalidateQueries({
            queryKey: getGetDepartmentByIDQueryKey(membership.department_id),
          }),
        ),
      );

      toast.success(
        {
          title: formatMessage({
            defaultMessage: `Resenären har tagits bort från avdelningen`,
          }),
        },
        { autoClose: 3000 },
      );

      // The traveller has left the organisation and can no longer be fetched,
      // so leave the page instead of refetching it.
      await navigate({
        to: '/$tenantId/departments',
        params: { tenantId },
        search: {
          departmentId: traveller.memberships[0].department_id,
        },
        replace: false,
      });
      queryClient.removeQueries({
        queryKey: getGetTravellerByIDQueryKey(traveller.traveller.id),
      });
    } catch (error) {
      console.error(error);
      const isPartialFailure =
        isPartialDeleteTravellerProductsError(error) ||
        associatedProductsDeleted;
      toast.error(
        {
          title: isPartialFailure
            ? formatMessage({
                defaultMessage:
                  'Kunde inte slutföra borttagningen. Vissa biljetter, kort eller Allpay-konton kan redan ha tagits bort. Resenären har inte tagits bort från avdelningarna.',
              })
            : formatMessage({ defaultMessage: 'Ett fel uppstod' }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    deleteAssociatedProducts,
    deleteCollectionMember,
    formatMessage,
    navigate,
    queryClient,
    tenantId,
    traveller,
  ]);

  const deleteWarningMessage = (
    <>
      <FormattedMessage defaultMessage="Resenären tas bort från alla avdelningar. Alla biljetter köpta i portalen avslutas automatiskt." />{' '}
      {hasNonSchoolMemberships && (
        <FormattedMessage defaultMessage="Tjänsteresa i app och tjänsteresa på kort stängs av automatiskt." />
      )}
    </>
  );

  if (!checkUserPermissions({ permission: 'organization:editor' })) {
    return (
      <div className="flex flex-col gap-4">
        <i>
          <FormattedMessage defaultMessage="Enbart en organisationadministratör kan nyttja dessa funktioner. Är resenären bara med i en avdelning och du är administratör för den avdelningen kan du radera/inaktivera resenären från avdelningen, vilket blir samma resultat." />
        </i>
        <FormSection
          title={formatMessage({ defaultMessage: 'Inaktivera' })}
          editMode={false}
        >
          <i>
            <FormattedMessage defaultMessage="Du har inte rättighet att inaktivera eller återaktivera resenären." />
          </i>
        </FormSection>
        <FormSection
          title={formatMessage({ defaultMessage: 'Radera' })}
          editMode={false}
        >
          {deleteWarningMessage}
          <i>
            <FormattedMessage defaultMessage="Du har inte rättighet att radera resenären." />
          </i>
        </FormSection>
      </div>
    );
  }

  const status = getTravellerStatus(traveller);

  return (
    <>
      {status !== 'inactive' && (
        <TravellerInactivateForm traveller={traveller} mode="inactivate" />
      )}
      {status !== 'active' && (
        <TravellerInactivateForm traveller={traveller} mode="activate" />
      )}
      <DeleteConfirmationForm
        title={formatMessage({ defaultMessage: 'Radera' })}
        warningMessage={deleteWarningMessage}
        canDelete={true}
        onDelete={onDeleteTraveller}
      />
    </>
  );
};
